// Mock User API Service for Development/Testing
// This demonstrates the expected API structure for the backend implementation

class MockUserApiService {
  constructor() {
    this.users = new Map(); // In-memory storage for demo purposes
    this.nextUserId = 1;
  }

  // Mock POST /api/v1/users endpoint
  async createUser(userData) {
    console.log('🔧 Mock API: Creating user with data:', userData);
    
    // Simulate API validation
    if (!userData.name || !userData.email || !userData.telephone) {
      throw new Error('Missing required fields: name, email, telephone');
    }

    // Simulate email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      throw new Error('Invalid email format');
    }

    // Create mock user record
    const userId = this.nextUserId++;
    const user = {
      id: userId,
      user_id: userId, // Alternative ID format
      name: userData.name,
      email: userData.email.toLowerCase(),
      telephone: userData.telephone,
      accepts_promotional_emails: userData.accepts_promotional_emails || false,
      source: userData.source || 'web',
      metadata: userData.metadata || {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: 'active'
    };

    // Store user
    this.users.set(userId, user);
    
    console.log('✅ Mock API: User created successfully:', user);
    
    // Simulate API response delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return user;
  }

  // Mock GET /api/v1/users/:id endpoint
  async getUser(userId) {
    console.log('🔧 Mock API: Getting user:', userId);
    
    const user = this.users.get(parseInt(userId));
    if (!user) {
      throw new Error('User not found');
    }
    
    return user;
  }

  // Mock PATCH /api/v1/users/:id endpoint
  async updateUser(userId, updateData) {
    console.log('🔧 Mock API: Updating user:', userId, updateData);
    
    const user = this.users.get(parseInt(userId));
    if (!user) {
      throw new Error('User not found');
    }

    // Update user data
    const updatedUser = {
      ...user,
      ...updateData,
      updated_at: new Date().toISOString()
    };

    this.users.set(parseInt(userId), updatedUser);
    
    console.log('✅ Mock API: User updated successfully:', updatedUser);
    return updatedUser;
  }

  // Mock GET /api/v1/users endpoint
  async listUsers(filters = {}) {
    console.log('🔧 Mock API: Listing users with filters:', filters);
    
    let users = Array.from(this.users.values());
    
    // Apply filters
    if (filters.source) {
      users = users.filter(user => user.source === filters.source);
    }
    
    if (filters.status) {
      users = users.filter(user => user.status === filters.status);
    }

    return {
      users,
      total: users.length,
      page: 1,
      per_page: 100
    };
  }

  // Get all stored users for debugging
  getAllUsers() {
    return Array.from(this.users.values());
  }
}

export default MockUserApiService;

/**
 * BACKEND IMPLEMENTATION REFERENCE:
 * 
 * Here's how you should implement the actual API endpoints in your backend:
 * 
 * 1. POST /api/v1/users
 *    - Create a new user in the database
 *    - Validate required fields (name, email, telephone)
 *    - Validate email format
 *    - Store promotional email preferences
 *    - Return created user with ID
 * 
 * 2. GET /api/v1/users/:id
 *    - Retrieve user by ID
 *    - Return user data or 404 if not found
 * 
 * 3. PATCH /api/v1/users/:id
 *    - Update existing user data
 *    - Validate updated fields
 *    - Return updated user data
 * 
 * 4. GET /api/v1/users
 *    - List users with optional filtering
 *    - Support pagination
 *    - Return array of users with metadata
 * 
 * Expected Request/Response Format:
 * 
 * POST /api/v1/users
 * Request Body:
 * {
 *   "name": "John Doe",
 *   "email": "john@example.com",
 *   "telephone": "+1234567890",
 *   "accepts_promotional_emails": true,
 *   "source": "boris_chat_widget",
 *   "metadata": {
 *     "widget_version": "1.0",
 *     "entry_point": "chat_widget",
 *     "timestamp": "2024-01-01T00:00:00Z"
 *   }
 * }
 * 
 * Response (201 Created):
 * {
 *   "id": 123,
 *   "user_id": 123,
 *   "name": "John Doe",
 *   "email": "john@example.com",
 *   "telephone": "+1234567890",
 *   "accepts_promotional_emails": true,
 *   "source": "boris_chat_widget",
 *   "metadata": {...},
 *   "created_at": "2024-01-01T00:00:00Z",
 *   "updated_at": "2024-01-01T00:00:00Z",
 *   "status": "active"
 * }
 * 
 * Database Schema Suggestion:
 * 
 * CREATE TABLE users (
 *   id SERIAL PRIMARY KEY,
 *   name VARCHAR(255) NOT NULL,
 *   email VARCHAR(255) UNIQUE NOT NULL,
 *   telephone VARCHAR(50) NOT NULL,
 *   accepts_promotional_emails BOOLEAN DEFAULT false,
 *   source VARCHAR(100) DEFAULT 'web',
 *   metadata JSONB,
 *   created_at TIMESTAMP DEFAULT NOW(),
 *   updated_at TIMESTAMP DEFAULT NOW(),
 *   status VARCHAR(20) DEFAULT 'active'
 * );
 */